import React, { useContext, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactModal from 'react-modal-resizable-draggable';
import { MobXProviderContext, observer } from 'mobx-react';
import { register } from '../../../i18n';
import toolbarService from '../../services/ToolbarService';
import RichTextEditor, { BASIC_CKEDITOR_TOOLBAR, MATH_TOOLBAR } from '../RichTextEditor';
import { LessonMode } from '../../../Constants';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import studentActivityService from '../../services/StudentActivityService';
import DialogPositionService from '../../services/DialogPositionService';
import '../../../css/NotepadTool.scss';
import '../../../css/Accessibility.scss';
import useReactModalResizeableDraggable from '../../../hooks/useReactModalResizeableDraggable';

const t = register('ToolNames');
const t2 = register('GlobalQuestionLabels');
// const t3 = register('AriaLabels');

const Notepad = observer(() => {
  const {
    toolbarManager,
    studentActivityManager,
    lessonManager
  } = useContext(MobXProviderContext);

  // Focus on the modal when the notepad dialog opens, then focus on editor when ready
  useEffect(() => {
    // Focus on the flexible-modal first
    setTimeout(() => {
      const flexibleModal = document.querySelector('.notepadTool:not(.contentNotepadTool) .flexible-modal');
      if (flexibleModal) {
        flexibleModal.setAttribute('tabindex', '0');
        flexibleModal.focus();
      }
    }, 100);
  }, []);

  const handleEditorReady = (editor) => {
    // Focus on the editor after it's ready
    setTimeout(() => {
      if (editor?.editing?.view?.focus) {
        editor.editing.view.focus();
      }
    }, 150);
  };

  // Announce when notepad dialog opens
  useEffect(() => {
    const announcementElement = document.getElementById('notepad-dialog-announcement');
    if (announcementElement) {
      announcementElement.textContent = 'Notepad dialog opened';
    }
  }, []);

  const notePadSettings = toolbarService.getButtonSettings('Notepad');

  const isReadOnly = (lessonManager.playerMode !== LessonMode.ACTIVITY_MODE && lessonManager.playerMode !== LessonMode.PREVIEW);

  const toolbar = BASIC_CKEDITOR_TOOLBAR;
  const mathToolbar = MATH_TOOLBAR;
  const [enteredText, setEnteredText] = useState((studentActivityManager) ? studentActivityManager.activityNoteText : '');
  const saveNoteRef = useRef();

  useAccessibilityClick(saveNoteRef, (e) => {
    if (lessonManager.playerMode === LessonMode.ACTIVITY_MODE) {
      // If the text that was entered is the same as the text that is currently saved there is no need to save again.
      if (enteredText !== studentActivityManager.activityNoteText) {
        studentActivityService.saveActivityNote(enteredText);
      }
    }
    // Remove focus from the button after click to prevent it from staying expanded
    if (saveNoteRef.current) {
      saveNoteRef.current.blur();
    }
  });

  const closeNoteRef = useRef();
  useAccessibilityClick(closeNoteRef, (e) => {
    toolbarManager.setIsNotepadOpen(false);
  });

  useReactModalResizeableDraggable('notepadTool');

  return (
    createPortal(
      <div className='notepadTool'>
        <ReactModal
          disableKeystroke
          initHeight={300}
          initWidth={280}
          isOpen={true}
          left={5}
          onRequestClose={() => toolbarManager.setIsNotepadOpen(false)}
          top={DialogPositionService.getDefaultTopPosition()}
          role="dialog"
          aria-labelledby="notepad-dialog-title">
          <div className='note-body'>
            <div className='tool-header'>
              <h2 className='title-label basic' id='notepad-dialog-title'>{t('notepadLabel')}</h2>
            </div>
            <div className='editor-wrapper' aria-label={t('notepadLabel')}>
              <div className='cke_topspace'>
                <RichTextEditor data={(studentActivityManager) ? studentActivityManager.activityNoteText : ''}
                  onChange={(data) => setEnteredText(data)}
                  onReady={handleEditorReady}
                  readOnly={isReadOnly}
                  tabIndex={-1}
                  toolbar={(notePadSettings.ckeditor === 'math') ? mathToolbar : toolbar}
                  skipRole={true} />
              </div>
            </div>
            <div className='footer-buttons'>
              {(!isReadOnly) ?
                <button ref={saveNoteRef} className='submit-response primary'>{t2('save')}</button> : ''}
            </div>
          </div>
        </ReactModal>
      </div>, document.getElementById('the-body'))
  );
});

export default Notepad;
